import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, ArrowLeft } from 'lucide-react';

const Privacy = () => {
  return (
    <div className="pt-16">
      <section className="relative py-20 bg-gradient-to-br from-primary-600 via-accent-600 to-primary-700 text-white">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Shield className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-4">Privacy Policy</h1>
          <p className="text-xl text-primary-100">Last updated: December 2025</p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/home" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8 font-medium">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p className="text-gray-600 mb-4">
              MMI Online ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our AI-powered business automation services, including Database Reactivation, Speed To Lead, Out Of Hours support, Google Reviews AI, Voice AI, and Abandoned Cart Recovery.
            </p>
            <p className="text-gray-600 mb-4">
              Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site or use our services.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">2.1 Personal Data</h3>
            <p className="text-gray-600 mb-4">We may collect personally identifiable information that you voluntarily provide to us when you:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Fill out contact forms on our website</li>
              <li>Subscribe to our newsletter or marketing communications</li>
              <li>Request a demo or consultation</li>
              <li>Sign up for our services</li>
              <li>Communicate with us via email, phone, or other channels</li>
            </ul>
            <p className="text-gray-600 mb-4">This information may include:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Name and contact details (email address, phone number)</li>
              <li>Company name and job title</li>
              <li>Billing and payment information</li>
              <li>Business data you provide for our AI services to process</li>
              <li>Communication preferences</li>
            </ul>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">2.2 Customer Database Information</h3>
            <p className="text-gray-600 mb-4">
              When you use our Database Reactivation, Speed To Lead, or other AI services, you may provide us with access to your customer databases. This data may include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Customer names and contact information</li>
              <li>Purchase history and lead status</li>
              <li>Communication history</li>
              <li>Other business-related customer data</li>
            </ul>
            <p className="text-gray-600 mb-4">
              We process this data solely on your behalf and in accordance with your instructions to provide our services.
            </p>

            <h3 className="text-xl font-display font-semibold text-gray-900 mt-6 mb-3">2.3 Automatically Collected Information</h3>
            <p className="text-gray-600 mb-4">When you visit our website, we may automatically collect:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>IP address and browser type</li>
              <li>Device information and operating system</li>
              <li>Pages visited and time spent on our site</li>
              <li>Referring website addresses</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">We use the information we collect to:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Provide, operate, and maintain our AI services</li>
              <li>Process your transactions and send related information</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Send you marketing and promotional communications (with your consent)</li>
              <li>Improve our website and services</li>
              <li>Analyze usage patterns and trends</li>
              <li>Protect against fraudulent or unauthorized activity</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">4. Data Processing for AI Services</h2>
            <p className="text-gray-600 mb-4">
              Our AI-powered services (Database Reactivation, Speed To Lead, Out Of Hours, Google Reviews, Voice AI, and Abandoned Cart) process data to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Send automated SMS and email communications on your behalf</li>
              <li>Engage with your leads and customers using conversational AI</li>
              <li>Handle inbound calls and manage appointments</li>
              <li>Respond to Google reviews automatically</li>
              <li>Follow up with abandoned cart customers</li>
              <li>Qualify leads and schedule appointments</li>
            </ul>
            <p className="text-gray-600 mb-4">
              You remain the data controller for any customer data you provide. We act as a data processor, processing data only according to your instructions and for the purposes of providing our services.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-600 mb-4">We may share your information with:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Service Providers:</strong> Third-party vendors who assist us in providing our services (e.g., SMS gateways, cloud hosting, payment processors)</li>
              <li><strong>Business Partners:</strong> With your consent, for joint marketing initiatives</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="text-gray-600 mb-4">
              We do not sell your personal information to third parties.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">6. Data Security</h2>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure access controls and authentication</li>
              <li>Regular security assessments and audits</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
            <p className="text-gray-600 mb-4">
              However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">7. Data Retention</h2>
            <p className="text-gray-600 mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. When you terminate your use of our services, we will delete or anonymize your data within a reasonable timeframe, unless retention is required for legal or legitimate business purposes.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">8. Your Rights</h2>
            <p className="text-gray-600 mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Restriction:</strong> Request restriction of processing of your data</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Objection:</strong> Object to processing of your personal data</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-600 mb-4">
              To exercise these rights, please contact us using the details provided below.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">9. Cookies and Tracking</h2>
            <p className="text-gray-600 mb-4">
              We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie preferences through your browser settings. For more information, please see our Cookie Policy.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">10. Third-Party Links</h2>
            <p className="text-gray-600 mb-4">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">11. Children's Privacy</h2>
            <p className="text-gray-600 mb-4">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child, we will take steps to delete it promptly.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">12. International Data Transfers</h2>
            <p className="text-gray-600 mb-4">
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with applicable data protection laws.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">13. Changes to This Policy</h2>
            <p className="text-gray-600 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
            </p>

            <h2 className="text-2xl font-display font-bold text-gray-900 mt-8 mb-4">14. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have any questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <p className="text-gray-700 font-semibold">MMI Online</p>
              <p className="text-gray-600">Email: privacy@mmionline.com</p>
              <p className="text-gray-600">Phone: +44 (0) 123 456 7890</p>
            </div>

            <div className="flex items-center justify-center space-x-4 mt-12 pt-8 border-t border-gray-200">
              <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
                View Terms & Conditions
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/contact" className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium">
                <Mail className="w-4 h-4 mr-2" />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
